import { PROCESS_REDUX_ACTION_GENERAL_ERROR, WORKING_ORDER } from "constants/action-types";
import _ from "lodash";
import siteConfig from "site-config";
import { fetchProductsAvailability, fetchWorkingProducts } from "actions/working-order/products";
import { IRequestDataAction, progressEpic } from "epics/base-epic";
import { format } from "lib/globalize-util";
import { IAddLineItemResponse } from "types/working-order/line-items";
import { sendAddLineItemResult } from "actions/working-order/line-items";
import { add as onSnackbarAdd } from "actions/snackbars";
import { cartFetch } from "actions/cart";
import { updateWorkingOrderState } from "actions/working-order-state";
import type { Store } from "redux";
import type { IStore } from "reducers";
import App from "lib/app";

export const addLineItemEpic = progressEpic<IRequestDataAction, IAddLineItemResponse>(
    WORKING_ORDER.LINE_ITEM.ADD,
    ({ payload }) => ({
        method: "POST",
        body: payload.body,
        url: `${siteConfig.api}/api/v2.0/orders/working/line-items`,
    }),
    {
        mapper: "mergeMap",
        checkErrorStatus: ({ xhr }) => {
            throw Error(
                _.get(xhr, "response.message", format("Oops! Something went wrong. Please refresh and try again."))
            );
        },
    }
);

export const lineItemsAmountChangeEpic = (action$) =>
    action$
        .ofType(WORKING_ORDER.LINE_ITEM.ADD.SUCCESS)
        .filter((action) => !_.isEmpty(action?.payload?.lineItems))
        .switchMap(() => [cartFetch()]);

export const displaySnackbarEpic = (action$) =>
    action$
        .ofType(WORKING_ORDER.LINE_ITEM.ADD.SUCCESS)
        .filter((action) => !_.isEmpty(action?.meta?.snackbar))
        .mergeMap((action) => [onSnackbarAdd(action?.meta?.snackbar)]);

export const lineItemAddSuccessEpic = (action$) =>
    action$
        .ofType(WORKING_ORDER.LINE_ITEM.ADD.SUCCESS)
        .filter((action) => action?.meta?.notifyCart)
        .mergeMap(({ payload, meta }) =>
            _.isEmpty(payload?.lineItems)
                ? [sendAddLineItemResult({ rejectedItemsGroupedByCause: payload?.rejectedItemsGroupedByCause }, meta)]
                : [
                      fetchWorkingProducts(
                          { ids: _.map(payload?.lineItems, "product") },
                          { ...meta, addLineItemsResult: { lineItems: payload?.lineItems } }
                      ),
                      fetchProductsAvailability(
                          { ids: _.map(payload?.lineItems, "product") },
                          { ...meta, addLineItemsResult: { lineItems: payload?.lineItems } }
                      ),
                  ]
        );

export const addLineItemErrorEpic = (action$) =>
    action$
        .ofType(WORKING_ORDER.LINE_ITEM.ADD.ERROR)
        .filter((action) => action?.meta?.notifyCart)
        .mergeMap(({ payload, meta, error }) => [sendAddLineItemResult(payload, meta, error)]);

export const fetchItemDataErrorEpic = (action$) =>
    action$
        .ofType(WORKING_ORDER.AVAILABILITY.FETCH.ERROR, WORKING_ORDER.PRODUCTS.FETCH.ERROR)
        .filter((action) => action?.meta?.addLineItemsResult)
        .mergeMap(({ payload, meta, error }) => {
            App.legacy.sendMessage({
                action: PROCESS_REDUX_ACTION_GENERAL_ERROR,
                meta,
                payload,
                error,
            });
            return [];
        });

export const addedProductDataSuccessEpic = (action$, store: Store<IStore>) =>
    action$
        .ofType(WORKING_ORDER.AVAILABILITY.FETCH.SUCCESS, WORKING_ORDER.PRODUCTS.FETCH.SUCCESS)
        .filter((action) => action?.meta?.addLineItemsResult)
        .mergeMap(({ meta }) => {
            const { working } = store.getState();

            if (working?.products?.isFetching || working?.availability?.isFetching) {
                return [];
            }

            const productIds = _.map(meta?.addLineItemsResult?.lineItems, "product");
            const productsToLoad = _.difference(productIds, _.keys(working?.products?.payload));
            const productsWithoutAvailability = _.uniq(_.difference(productIds, working?.availability?.cache));

            const requireProducts = !_.isEmpty(productsToLoad);
            const requireAvailability =
                !_.isEmpty(productsWithoutAvailability) && _.isEmpty(working?.availability?.error);

            return requireProducts || requireAvailability
                ? []
                : [
                      updateWorkingOrderState(
                          {
                              availability: working?.availability?.payload,
                              products: _.values(_.pick(working?.products?.payload, productIds)),
                          },
                          meta
                      ),
                      sendAddLineItemResult(meta?.addLineItemsResult, meta),
                  ];
        });
